import Dexie, { type EntityTable } from 'dexie'
import { appConfig } from '@/config/environment'
import type { RegistrationForm } from '@/features/forms/types'
import type { ContactInput, RegisteredContact, RegistrationAnswerInput } from '@/features/registrations/registrationService'
import type { SessionWithEvent } from '@/features/sessions/types'
import type { EventRecord } from '@/features/events/types'

/**
 * A registration waiting on this device to reach the server.
 *
 * `pending` will be (re)sent, `syncing` is in flight, and `error` needs a
 * person's attention: the server refused it for a reason a retry cannot fix.
 * A record leaves the store only once the server has accepted it, or when a
 * person explicitly discards it; never because a sync was attempted.
 */
export type QueuedRegistrationStatus = 'pending' | 'syncing' | 'error'

export type QueuedRegistration = {
  clientRegistrationId: string
  /** The collector who registered the attendee; only their sign-in may sync it. */
  userId: string
  sessionId: string
  eventId: string
  ticketed?: boolean
  /**
   * Set for a walk-up registered at the Box Office: they are checked in as
   * they're registered, so this registration syncs through Box Office (no
   * session of the collector's) with its own check-in id.
   */
  boxOffice?: { clientCheckInId: string; savedOffline: boolean } | undefined
  formId: string
  contact: ContactInput
  answers: RegistrationAnswerInput[]
  capturedAt: string
  status: QueuedRegistrationStatus
  attempts: number
  lastAttemptAt: string | null
  nextAttemptAt: string | null
  lastError: string | null
}

/** What a device needs to keep registering in a session without a connection. */
export type SessionPack = {
  userId: string
  sessionId: string
  session: SessionWithEvent
  /** The latest published form the device has seen for the session's event. */
  form: RegistrationForm | null
  cachedAt: string
}

export type JoinedSessionsCache = {
  userId: string
  sessions: SessionWithEvent[]
  cachedAt: string
}

/**
 * The collector's synced contacts at an event, as last loaded online, so All
 * Mine can still list them offline. Dropped when that person signs out.
 */
export type MyContactsCache = {
  userId: string
  eventId: string
  contacts: RegisteredContact[]
  cachedAt: string
}

/**
 * The events last loaded by one signed-in person. Event management remains
 * online-only; this snapshot only lets the Events list and known event details
 * remain readable when the connection drops.
 */
export type EventSnapshot = {
  userId: string
  events: EventRecord[]
  cachedAt: string
}

/** One attendee as the door needs them: enough to recognise and find them, little more. */
export type CheckInEntry = {
  registrationId: string
  /**
   * The id this registration had when it was first written on a device. It is
   * how a synced Box Office walk-up is recognised after the server gives it
   * its own registration id.
   */
  clientRegistrationId?: string | null
  firstName: string
  lastName: string | null
  /** Only the last four digits ever reach the device. */
  phoneLast4: string | null
  ticketId: string | null
  ticketReference: string | null
  /** A walk-up registered on this device that hasn't synced yet: its id is the client registration id. */
  pendingRegistration?: boolean
  /** SHA-256 of the ticket token, to recognise a scanned QR code; never the token. */
  ticketTokenDigest: string | null
  /** SHA-256 of the ticket's short secret, for short QR codes. Absent in lists downloaded before short links. */
  ticketShortDigest?: string | null
  checkInCount: number
  firstCheckedInAt: string | null
  lastCheckedInAt: string | null
  /** Display name of the person who most recently checked this attendee in. */
  lastCheckedInBy?: string | null
}

/** An event's check-in dataset, downloaded so the door keeps working offline. */
export type CheckInDataset = {
  userId: string
  eventId: string
  eventName: string
  eventLocator: string
  /** The event's short code, as in short ticket links. Absent in lists downloaded before short links. */
  eventCode?: string
  /** The event's time zone, so Box Office phone numbers start on its country. */
  eventTimezone?: string | null
  mode: 'single' | 'multiple'
  /** The event's registration form, so the Box Office works offline. */
  form?: RegistrationForm | null
  entries: CheckInEntry[]
  downloadedAt: string
}

/**
 * A check-in made on this device and not yet confirmed by the server. A QR
 * check-in made offline is stored against the registration it matched, so the
 * ticket's bearer token is never kept on the device.
 */
export type QueuedCheckIn = {
  clientCheckInId: string
  userId: string
  eventId: string
  registrationId: string
  attendeeName: string
  method: 'qr' | 'manual'
  checkedInAt: string
  status: 'pending' | 'syncing' | 'error'
  attempts: number
  nextAttemptAt: string | null
  lastError: string | null
}

export type OfflineDb = Dexie & {
  registrations: EntityTable<QueuedRegistration, 'clientRegistrationId'>
  sessionPacks: EntityTable<SessionPack>
  joinedSessions: EntityTable<JoinedSessionsCache, 'userId'>
  myContacts: EntityTable<MyContactsCache>
  eventSnapshots: EntityTable<EventSnapshot, 'userId'>
  checkInDatasets: EntityTable<CheckInDataset>
  checkIns: EntityTable<QueuedCheckIn, 'clientCheckInId'>
}

/**
 * The device's operational store (IndexedDB). Its name carries the
 * environment and backend identity, so Local and Development data can never
 * meet, and nothing here is synchronised anywhere but the backend it belongs to.
 */
export function createOfflineDb(name = `${appConfig.storageNamespace}:offline`): OfflineDb {
  const db = new Dexie(name) as OfflineDb
  db.version(1).stores({
    registrations: 'clientRegistrationId, [userId+status], [userId+eventId], capturedAt',
    sessionPacks: '[userId+sessionId], userId',
    joinedSessions: 'userId',
  })
  db.version(2).stores({ myContacts: '[userId+eventId], userId' })
  db.version(3).stores({
    checkInDatasets: '[userId+eventId], userId',
    checkIns: 'clientCheckInId, [userId+status], [userId+eventId], checkedInAt',
  })
  db.version(4).stores({ eventSnapshots: 'userId' })
  return db
}

export const offlineDb = createOfflineDb()
